import AsyncControl from '@/Controls/AsyncControl';
import ComponentManager from '@/Management/ComponentManager';
import DetailViewGroupBoxViewModelType from '@/ViewModels/Controls/DetailViewGroupBoxViewModel';
import GroupFactory from '@/Factories/GroupFactory';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import LayoutFactory from '@/Factories/LayoutFactory';
import Utilities from '@/Utility/Utilities';
import { IDetailViewGroupDTO } from "coreframetypings/dto";
import { DisposeBehavior, IAsyncUserInteractable } from '#/DefaultViewExtensionInterfaces';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import ControlFactory from '@/Factories/ControlFactory';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';

export default class DetailViewGroupBox extends AsyncControl {

    protected $jQueryControl: JQuery;
    protected $content: JQuery;
    private groupFactories: Array<GroupFactory>;
    private layoutFactory: LayoutFactory;

    public constructor(private viewModel: DetailViewGroupBoxViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.groupFactories = [];
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.$jQueryControl != null) {
            return this.$jQueryControl;
        }

        this.$jQueryControl = this.element || $('<div>');
        this.$content = $('<div class="c-content">').appendTo(this.$jQueryControl);

        this.applyBindings(this.$jQueryControl, {
            controlName: 'detailviewgroupbox',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            visible: this.viewModel.isVisible
        });

        await this.refreshAsync();

        if (this.isDisposed || this.viewModel.isDisposed)
            return this.$jQueryControl;

        this.defineSubscription(this.viewModel.context._refresh, () => {
            this.groupFactories.forEach((g: GroupFactory) => {
                if (!g.isDisposed)
                    g.dispose();
            });

            this.groupFactories = [];

            if (this.layoutFactory.isDisposed === false) {
                this.layoutFactory.dispose();
                this.layoutFactory = null;
            }

            this.viewModel.context.removeDefaultIterators().then(async () => {
                await this.refreshAsync();
            });
        });

        this.widget(null);

        return this.$jQueryControl;
    }

    public onAfterRender() {
        this.focusFirstElement();
        this.restoreScrollPosition();
    }

    private restoreScrollPosition(): void {
        if (this.isDisposed)
            return;

        const anchor = this.viewModel?.context?.viewModel?.getViewAnchor();
        if (!anchor)
            return;

        // Es wird erst gescrollt, wenn die Daten in den Grids etc. geladen sind. Falls der User in der Zeit
        // selbst angefangen hat zu scrollen, soll seine Scroll-Position nicht überschrieben werden.
        let hasUserScrolled = false;
        const $jQueryControl = this.$jQueryControl;
        const wheelEventHandler = () => {
            hasUserScrolled = true;
            $jQueryControl.off('wheel', wheelEventHandler);
        };
        $jQueryControl.on('wheel', wheelEventHandler);

        // Erst, wenn die Daten in den Grids etc. geladen sind, sollte gescrollt werden.
        // Ansonsten könnte die Scroll-Position durch neue Grid-Zeilen o.Ä. verschoben werden.
        RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
            window.setTimeout(() => {
                $jQueryControl.off('wheel', wheelEventHandler);
                if (hasUserScrolled || this.isDisposed)
                    return;

                this.scrollToItem(anchor);
            });
        });
    }

    private scrollToItem(item: string): void {
        if (!item || this.isDisposed || !this.$jQueryControl?.length)
            return;

        const factory = this.viewModel.context.getUIFactory<ControlFactory>(item);
        const $control = factory?.tuple?.control;
        if ($control?.length) {
            const scrollTop = $control.offset().top - this.$jQueryControl.offset().top;
            const scrollContainerHeight = this.$jQueryControl[0].clientHeight;
            this.scrollTo(scrollTop - scrollContainerHeight / 2);
        }
    }

    protected scrollTo(scrollTop: number): void {
        this.$jQueryControl.scrollTop(scrollTop);
    }

    private async refreshAsync() {
        if (this.isDisposed || this.viewModel.isDisposed || this.viewModel.context.isDisposed)
            return;

        await this.viewModel.executeBusilyAsync(async () => {
            this.$content.empty();

            const tuples = await this.createGroupsAsync();
            if (this.isDisposed)
                return;

            const controls = tuples
                .map(t => t?.factory)
                .filter(t => t?.isDisposed === false)
                .map(f => f.control)
                .filter(c => c?.isDisposed === false);

            for (const control of controls) {
                if (this.isAsyncUserInteractable(control)) {
                    control.userInteractionPossibleNotifier.onStateReached(() => {
                        if (AsyncTypeGuards.isAsyncElementProvider(control)) {
                            control.getElementAsync().then(() => this.focusFirstElement());
                        } else {
                            this.focusFirstElement();
                        }
                    });
                }
            }

            const $control = await this.createItemAsync(tuples, this.createLayoutAsync);
            if (this.isDisposed)
                return;

            this.$content.append($control);
        });
    }

    private focusFirstElement() {
        Utilities.focusFirstFocusableElement(this.$jQueryControl.find('input'));
    }

    private isAsyncUserInteractable(obj: any): obj is IAsyncUserInteractable {
        return (obj as IAsyncUserInteractable)?.userInteractionPossibleNotifier != null;
    }

    private async createGroupsAsync(): Promise<GroupFactoryTuple[]> {
        const tuples: GroupFactoryTuple[] = [];
        
        const createdGroups = await this.createItemsAsync(this.viewModel.detailViewGroups, this.createGroupAsync);
        if (this.isDisposed)
            return tuples;

        for (const { groupFactory, tuple } of createdGroups) {
            this.groupFactories.push(groupFactory);
            tuples.push(tuple);
        }
        return tuples;
    }

    private async createGroupAsync(g: IDetailViewGroupDTO) {
        if (this.isDisposed)
            return null;

        return GroupFactory.createInstanceAsync(g, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator
        });
    }

    private async createLayoutAsync(tuples: GroupFactoryTuple[]) {
        if (this.isDisposed)
            return null;

        const { tuple } = await LayoutFactory.createInstanceAsync(this.viewModel.layout, this.viewModel.context, tuples, {});
        return tuple?.control;
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('DetailViewGroupBox', DetailViewGroupBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');